<script setup lang="ts">
import type { BlossomCarousel } from '@blossom-carousel/vue'
import type { BlockHeroFragment, MediaFragment } from '#graphql-operations'

interface BlockHeroProps {
  data: BlockHeroFragment
}

const props = defineProps<BlockHeroProps>()
const medias = computed(() => props.data.media as MediaFragment[])
const monogram = computed(() => props.data.monogram[0] as MediaFragment)
const colorScheme = useColorScheme(() => props.data.colorScheme)

const isCentered = computed(() =>
  [props.data.heading, monogram.value, props.data.tagline].filter(Boolean).length <= 1,
)
</script>

<template>
  <Section class="blockHero c-$color-scheme-foreground bg-$color-scheme-background grid lg:grid-cols-2" :class="colorScheme">
    <div class="caption px-3.75 py-12 text-center min-h-50svh lg:px-18.5" :class="{ 'lg:order-last': data.reverse }">
      <div
        class="mx-auto flex flex-col gap-12 h-full max-w-4xl lg:gap-14 2xl:max-w-5xl"
        :class="{ 'justify-center': isCentered, 'justify-between': !isCentered }"
      >
        <Text v-if="data.heading" class="richtext-simple typo-serif-4" :html="data.heading" />
        <CraftSvg v-if="monogram" class="mx-auto w-18 lg:w-23" :media="monogram" />
        <Text v-if="data.tagline" class="richtext-simple typo-serif-2 mx-auto shrink-0 max-w-xl space-y-1lh" :html="data.tagline" />
      </div>
    </div>
    <div class="media">
      <BlossomCarousel class="carousel flex h-auto w-full lg:size-full">
        <div v-for="media in medias" :key="media.id" data-blossom-slide class="slide flex-shrink-0 w-full aspect-1 lg:size-full lg:aspect-auto">
          <CraftMedia class="size-full object-cover" :media :img-props="{ alt: data.headingText, sizes: '1024px lg:50vw' }" />
        </div>
      </BlossomCarousel>
    </div>
  </Section>
</template>

<style scoped>
.blockHero .caption {
  @screen lg {
    height: 100%;
    min-height: calc(100svh - var(--header-height));
    /* height: 0; */
  }
}

.blockHero .media {
  @screen lg {
    position: sticky;
    top: var(--header-height);
    height: calc(100svh - var(--header-height));
  }
}

.blockHero .carousel {
  overflow: auto hidden;
  scrollbar-width: none;
  scroll-snap-type: x mandatory;
}

.blockHero :deep(.remote-svg > path) {
  fill: var(--color-scheme-foreground);
}

.slide {
  scroll-snap-align: start;
}
</style>
